import { useCallback, useEffect, useMemo, useState } from "react";

import { MenuCategoryDto, MenuItemListDto } from "services/menu/restModels";

import { useMenuScope } from "shared/context/MenuScopeContext";
import { useConstants } from "shared/hooks/useConstants";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuCategories } from "shared/hooks/useMenuCategories";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import { localizedText, matchesLocalizedQuery } from "shared/lib/localizedMap";
import { useNotificationSnackbar, UseNotificationSnackbarReturn } from "shared/ui";

import { SEARCH_DEBOUNCE_MS, STOP_LIST_PAGE_SIZE } from "features/StopList/constants";
import { useActiveTab } from "features/StopList/hooks/useActiveTab";
import { StopListItemsState, useStopListItems } from "features/StopList/hooks/useStopListItems";
import { useStopListMutations } from "features/StopList/hooks/useStopListMutations";
import { StopListPickerState, useStopListPicker } from "features/StopList/hooks/useStopListPicker";
import { StopListTab } from "features/StopList/types";

export interface StopListState {
    menuId: number;
    locale: string;
    formatPrice: (price: number) => string;

    activeTab: StopListTab;
    setActiveTab: (tab: StopListTab) => void;

    itemsQuery: string;
    setItemsQuery: (value: string) => void;
    categoryFilterId: number | null;
    setCategoryFilterId: (menuCategoryId: number | null) => void;
    categoriesQuery: string;
    setCategoriesQuery: (value: string) => void;
    resetItemsFilters: () => void;

    itemsPage: number;
    setItemsPage: (page: number) => void;
    categoriesPage: number;
    setCategoriesPage: (page: number) => void;
    pageSize: number;

    list: StopListItemsState;
    stopCategories: MenuCategoryDto[];
    pagedStopCategories: MenuCategoryDto[];
    // Категории с позициями в стопе — опции селекта на вкладке позиций.
    categoryOptions: MenuCategoryDto[];
    categoryNameById: (menuCategoryId: number) => string;

    stopItemsCount: number;
    stopCategoriesCount: number;

    returnItemToSale: (item: MenuItemListDto) => void;
    returnCategoryToSale: (category: MenuCategoryDto) => void;
    pendingCategoryIds: Set<number>;

    pickerOpen: boolean;
    openPicker: () => void;
    closePicker: () => void;
    picker: StopListPickerState;

    loading: boolean;
    error: boolean;
    notify: UseNotificationSnackbarReturn;
}

const RETURN_MESSAGES = {
    success: "Убрано из стоп-листа",
    error: "Не удалось убрать из стоп-листа",
};

export const useStopListState = (): StopListState => {
    const { menuId } = useMenuScope();
    const locale = useLocale();
    const currencyId = useCurrencyId();
    const { constants } = useConstants();
    const notify = useNotificationSnackbar();
    const mutations = useStopListMutations(notify);

    const [activeTab, setActiveTab] = useActiveTab();

    const [itemsQuery, setItemsQuery] = useState<string>("");
    const [categoriesQuery, setCategoriesQuery] = useState<string>("");
    const [categoryFilterId, setCategoryFilterId] = useState<number | null>(null);
    const [itemsPage, setItemsPage] = useState<number>(1);
    const [categoriesPage, setCategoriesPage] = useState<number>(1);
    const [pendingCategoryIds, setPendingCategoryIds] = useState<Set<number>>(new Set());
    const [pickerOpen, setPickerOpen] = useState<boolean>(false);

    const {
        categories,
        summary,
        loading,
        error,
        refetch: refreshCategories,
        adjustStopListCount,
        setCategoryStopList,
    } = useMenuCategories(menuId);

    const debouncedItemsQuery = useDebouncedValue(itemsQuery, SEARCH_DEBOUNCE_MS);
    const effectiveItemsQuery = itemsQuery.trim() === "" ? "" : debouncedItemsQuery.trim();

    const list = useStopListItems({
        menuId,
        query: effectiveItemsQuery,
        categoryId: categoryFilterId,
        page: itemsPage,
        enabled: activeTab === "items",
    });

    // Смена условия выборки возвращает на первую страницу: иначе после сужения поиска
    // пользователь оказывается на пустой десятой странице.
    useEffect(() => {
        setItemsPage(1);
    }, [effectiveItemsQuery, categoryFilterId]);

    useEffect(() => {
        setCategoriesPage(1);
    }, [categoriesQuery]);

    const stopCategories = useMemo(
        () =>
            categories.filter(
                (category) =>
                    category.isInStopList &&
                    matchesLocalizedQuery(category.name, categoriesQuery)
            ),
        [categories, categoriesQuery]
    );

    const pagedStopCategories = useMemo(
        () =>
            stopCategories.slice(
                (categoriesPage - 1) * STOP_LIST_PAGE_SIZE,
                categoriesPage * STOP_LIST_PAGE_SIZE
            ),
        [stopCategories, categoriesPage]
    );

    // Последняя строка страницы вернулась в продажу — уходим на предыдущую, иначе
    // пользователь смотрит на пустую таблицу с активной кнопкой «назад».
    useEffect(() => {
        if (!list.loading && list.items.length === 0 && itemsPage > 1) {
            setItemsPage((page) => page - 1);
        }
    }, [list.loading, list.items.length, itemsPage]);

    useEffect(() => {
        if (pagedStopCategories.length === 0 && categoriesPage > 1) {
            setCategoriesPage((page) => page - 1);
        }
    }, [pagedStopCategories.length, categoriesPage]);

    const currencyFormatter = useMemo(
        () =>
            createCurrencyFormatter(
                constants?.currencies.find((item) => item.id === currencyId) || null,
                locale
            ),
        [constants, currencyId, locale]
    );

    const formatPrice = useCallback(
        (price: number) => currencyFormatter.toShow(price),
        [currencyFormatter]
    );

    const categoryNameById = useCallback(
        (menuCategoryId: number) => {
            const category = categories.find((item) => item.id === menuCategoryId);

            return category ? localizedText(category.name, locale) : "";
        },
        [categories, locale]
    );

    /*
    * Возврат в продажу — оптимистичный: строка уходит сразу, ответ ловим следом. Полного
    * перезапроса меню (как делал прежний стор) нет: на 200 позициях он давал заметную паузу
    * и подскок списка, а обновлять здесь нужно ровно одну строку и два счётчика.
    * */
    const returnItemToSale = useCallback(
        (item: MenuItemListDto) => {
            list.removeItem(item.id);
            adjustStopListCount(item.menuCategoryId, -1);

            mutations.setItemInStopList(item.id, false, RETURN_MESSAGES).then((ok) => {
                if (ok) return;

                adjustStopListCount(item.menuCategoryId, 1);
                list.reload();
            });
        },
        [list, adjustStopListCount, mutations]
    );

    const returnCategoryToSale = useCallback(
        (category: MenuCategoryDto) => {
            setPendingCategoryIds((current) => new Set(current).add(category.id));
            setCategoryStopList(category.id, false);

            mutations
                .setCategoryInStopList(category, false, RETURN_MESSAGES)
                .then((ok) => {
                    if (!ok) setCategoryStopList(category.id, true);
                })
                .finally(() => {
                    setPendingCategoryIds((current) => {
                        const next = new Set(current);
                        next.delete(category.id);
                        return next;
                    });
                });
        },
        [setCategoryStopList, mutations]
    );

    const picker = useStopListPicker({
        menuId,
        open: pickerOpen,
        categories,
        mutations,
        setCategoryStopList,
    });

    const { dirty: pickerDirty } = picker;
    const { reload: reloadItems } = list;

    const closePicker = useCallback(() => {
        setPickerOpen(false);

        // Диалог применяет изменения сразу, но списки страницы о них не знают: категории
        // правились точечно, а позиции добавлялись в стоп-лист мимо текущей выборки.
        if (pickerDirty) {
            refreshCategories();
            reloadItems();
        }
    }, [pickerDirty, refreshCategories, reloadItems]);

    const resetItemsFilters = useCallback(() => {
        setItemsQuery("");
        setCategoryFilterId(null);
    }, []);

    return {
        menuId,
        locale,
        formatPrice,

        activeTab,
        setActiveTab,

        itemsQuery,
        setItemsQuery,
        categoryFilterId,
        setCategoryFilterId,
        categoriesQuery,
        setCategoriesQuery,
        resetItemsFilters,

        itemsPage,
        setItemsPage,
        categoriesPage,
        setCategoriesPage,
        pageSize: STOP_LIST_PAGE_SIZE,

        list,
        stopCategories,
        pagedStopCategories,
        categoryOptions: categories.filter((category) => category.stopListItemsCount > 0),
        categoryNameById,

        stopItemsCount: summary.stopListItemsCount,
        stopCategoriesCount: summary.stopListCategoriesCount,

        returnItemToSale,
        returnCategoryToSale,
        pendingCategoryIds,

        pickerOpen,
        openPicker: () => setPickerOpen(true),
        closePicker,
        picker,

        loading,
        error,
        notify,
    };
};
